import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AlertTriangle,
    ArrowRight,
    CalendarClock,
    ClipboardList,
    FileDown,
    FileText,
    GraduationCap,
    Search,
    ShieldCheck
} from 'lucide-react';
import Logo from './Logo';
import Footer from './Footer';
import FAQ from './FAQ';
import './LandingPage.css';

const STAKES = [
    {
        label: 'Record request',
        title: 'Someone asks to see the records.',
        body: 'Training, incident, and related records need to be findable and reproducible.',
        close: 'Scattered files become a deadline.',
        icon: Search
    },
    {
        label: 'Incident or threat',
        title: 'Something happens at work.',
        body: 'Capture what happened and keep the relevant follow-up details together.',
        close: 'Memory is not a recordkeeping system.',
        icon: AlertTriangle
    },
    {
        label: 'Training and review',
        title: 'Responsibilities come due again.',
        body: 'Training-session records and plan-review work need ongoing attention.',
        close: 'A template does not remind you what you recorded.',
        icon: CalendarClock
    }
];

const REQUIREMENT_WORKFLOWS = [
    {
        label: 'SB 553 · Labor Code §6401.9',
        title: 'Workplace violence prevention records',
        body: 'Work from a plan template, record workplace-violence training sessions, document incidents, and download available PDF exports for supported records.',
        note: 'Plan, training-session, and incident recordkeeping',
        icon: ShieldCheck
    },
    {
        label: 'Harassment training · Gov. Code §12950.1',
        title: 'Harassment-training administration',
        body: 'Create employer-selected assignments, open official California Civil Rights Department training resources, and organize supported employer-review records.',
        note: 'Often associated with SB 1343',
        icon: GraduationCap
    }
];

const WORKFLOW_STEPS = [
    {
        title: 'Customize the plan template',
        detail: 'Start from one structured workplace violence prevention plan template.',
        icon: FileText
    },
    {
        title: 'Record completed training sessions',
        detail: 'Save the date, trainer, attendance count, and topics covered.',
        icon: GraduationCap
    },
    {
        title: 'Log incidents and follow-up details',
        detail: 'Keep incident facts and the follow-up information your business records together.',
        icon: ClipboardList
    },
    {
        title: 'Generate available PDF records',
        detail: 'Download the plan template and supported incident-record exports.',
        icon: FileDown
    }
];

const SIMPLE_STEPS = [
    {
        number: '01',
        title: 'Start with the free self-assessment',
        body: 'Answer five educational questions to identify topics worth reviewing for your workplace.'
    },
    {
        number: '02',
        title: 'Keep supported records together',
        body: 'Use one workspace for the plan template, completed training sessions, and incident records.'
    },
    {
        number: '03',
        title: 'Export what is available',
        body: 'Generate available PDF records when your business needs a practical copy.'
    }
];

function LandingPage() {
    const navigate = useNavigate();

    return (
        <div className="landing-page landing-home">
            <nav className="landing-nav" aria-label="Primary navigation">
                <div className="nav-content">
                    <button className="brand-home" onClick={() => navigate('/')} aria-label="CompCleared home">
                        <Logo size="small" />
                    </button>
                    <div className="nav-buttons">
                        <button className="btn-secondary" onClick={() => navigate('/login')}>
                            Log In
                        </button>
                        <button className="btn-primary" onClick={() => navigate('/signup')}>
                            Get Started
                        </button>
                    </div>
                </div>
            </nav>

            <main>
                <section className="problem-hero" aria-labelledby="landing-hero-title">
                    <div className="hero-ambient hero-ambient-one" aria-hidden="true" />
                    <div className="hero-ambient hero-ambient-two" aria-hidden="true" />
                    <div className="hero-grid" aria-hidden="true" />

                    <div className="problem-hero-inner">
                        <div className="hero-eyebrow">
                            <ShieldCheck size={16} aria-hidden="true" />
                            SB 553 + harassment-training records
                        </div>
                        <h1 id="landing-hero-title" className="problem-hero-title">
                            California workplace compliance, <span>organized.</span>
                        </h1>
                        <p className="problem-hero-lede">
                            SB 553 workplace-violence records and harassment-training records are separate
                            responsibilities. CompCleared gives small teams one practical workspace to organize the
                            supported work without pretending one workflow satisfies the other.
                        </p>
                        <div className="hero-actions">
                            <button className="btn-hero-primary" onClick={() => navigate('/exposure-check')}>
                                Start the SB 553 self-assessment <ArrowRight size={18} aria-hidden="true" />
                            </button>
                            <a className="btn-hero-secondary" href="#how-compcleared-works">
                                See how CompCleared works
                            </a>
                        </div>
                        <p className="hero-trust">
                            Free educational self-assessment <span aria-hidden="true">·</span> No signup required <span aria-hidden="true">·</span> Not legal advice
                        </p>
                    </div>
                </section>

                <section className="requirements-section" aria-labelledby="requirements-title">
                    <div className="section-shell">
                        <div className="section-heading compact-heading">
                            <span className="section-kicker">One workspace, clear boundaries</span>
                            <h2 id="requirements-title">Two requirements. Separate workflows.</h2>
                            <p>
                                Use a shared employee roster while keeping the purpose and records for each requirement
                                distinct. CompCleared organizes employer-entered information; it does not determine legal
                                coverage or whether a record is legally sufficient.
                            </p>
                        </div>

                        <div className="requirements-grid">
                            {REQUIREMENT_WORKFLOWS.map(({ label, title, body, note, icon: Icon }) => (
                                <article className="requirement-card" key={label}>
                                    <div className="requirement-icon" aria-hidden="true">
                                        <Icon size={25} />
                                    </div>
                                    <span className="requirement-label">{label}</span>
                                    <h3>{title}</h3>
                                    <p>{body}</p>
                                    <strong>{note}</strong>
                                </article>
                            ))}
                        </div>

                        <p className="requirements-boundary">
                            One training or document should not be assumed to satisfy both requirements.
                        </p>
                    </div>
                </section>

                <section id="the-problem" className="stakes-section" aria-labelledby="stakes-title">
                    <div className="section-shell">
                        <div className="section-heading section-heading-dark">
                            <span className="section-kicker">The problem</span>
                            <h2 id="stakes-title">
                                The worst time to organize the paperwork is when someone is already asking for it.
                            </h2>
                            <p>
                                Small-business teams can be responsible for an ongoing workplace program even without
                                a dedicated HR, legal, or safety department.
                            </p>
                        </div>

                        <div className="stakes-grid">
                            {STAKES.map(({ label, title, body, close, icon: Icon }, index) => (
                                <article className="stake-card" key={label}>
                                    <div className="stake-card-topline">
                                        <span>{String(index + 1).padStart(2, '0')} · {label}</span>
                                        <Icon size={20} aria-hidden="true" />
                                    </div>
                                    <h3>{title}</h3>
                                    <p>{body}</p>
                                    <strong>{close}</strong>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section id="how-compcleared-works" className="solution-section" aria-labelledby="solution-title">
                    <div className="solution-glow" aria-hidden="true" />
                    <div className="section-shell">
                        <div className="section-heading">
                            <span className="section-kicker section-kicker-green">The SB 553 workflow</span>
                            <h2 id="solution-title">
                                You provide the facts. <span>We organize the records.</span>
                            </h2>
                            <p>
                                CompCleared gives your business one place to work through the record types the product
                                currently supports—without pretending software can make legal decisions for you.
                            </p>
                        </div>

                        <div className="workflow-card">
                            <div className="workflow-card-header">
                                <div>
                                    <span className="workflow-label">Supported workplace-violence workflow</span>
                                    <h3>From an SB 553 starting template to practical records</h3>
                                </div>
                                <span className="workflow-status"><span aria-hidden="true" /> One workspace</span>
                            </div>

                            <div className="workflow-card-body">
                                <div className="workflow-track" aria-hidden="true">
                                    <div className="workflow-track-fill" />
                                </div>
                                <ol className="workflow-list" aria-label="Supported SB 553 workflow">
                                    {WORKFLOW_STEPS.map(({ title, detail, icon: Icon }, index) => (
                                        <li
                                            className="workflow-step"
                                            key={title}
                                            aria-label={`Workflow step ${index + 1}: ${title}`}
                                        >
                                            <div className="workflow-icon" aria-hidden="true">
                                                <Icon size={24} />
                                            </div>
                                            <span className="workflow-number">Step {index + 1}</span>
                                            <h4>{title}</h4>
                                            <p>{detail}</p>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="simple-section" aria-labelledby="simple-title">
                    <div className="section-shell">
                        <div className="section-heading compact-heading">
                            <span className="section-kicker">Dead simple by design</span>
                            <h2 id="simple-title">A clear next step—not another compliance binder.</h2>
                            <p>
                                Start with education, keep supported records organized, and download available outputs
                                without digging through disconnected files.
                            </p>
                        </div>

                        <div className="simple-grid">
                            {SIMPLE_STEPS.map(({ number, title, body }) => (
                                <article className="simple-card" key={number}>
                                    <span className="simple-number">{number}</span>
                                    <h3>{title}</h3>
                                    <p>{body}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="closing-section" aria-labelledby="closing-title">
                    <div className="closing-card">
                        <div className="closing-copy">
                            <span className="section-kicker section-kicker-light">Start with clarity</span>
                            <h2 id="closing-title">See where your business stands—then organize the records you maintain.</h2>
                            <p>
                                Begin with the free SB 553 educational self-assessment. When you are ready, CompCleared
                                Pro provides the separate supported workspaces for $19 per month or $149 per year.
                            </p>
                        </div>
                        <div className="closing-actions">
                            <button className="btn-cta" onClick={() => navigate('/exposure-check')}>
                                Start free self-assessment
                            </button>
                            <button className="btn-closing-secondary" onClick={() => navigate('/signup')}>
                                View CompCleared Pro <ArrowRight size={17} aria-hidden="true" />
                            </button>
                        </div>
                        <p className="closing-note">
                            Secure Stripe checkout · Monthly or annual billing · Manage your subscription through Stripe
                        </p>
                    </div>
                </section>

                <section className="faq-section" aria-labelledby="faq-title">
                    <h2 id="faq-title">Frequently asked questions</h2>
                    <FAQ />
                </section>
            </main>

            <Footer />
        </div>
    );
}

export default LandingPage;
